/**
 * SI-1-02 — gapless-seq client assertion (PRD §6.4).
 *
 * Pure helpers. No IO, no clock, no network. A client that sees `n` then
 * `n+2` must resync rather than continue reducing — the stream is gapless by
 * host construction, so a hole means the client missed actions (or is below
 * a compaction floor; SI-1-01/`session.compacted` owns that path).
 *
 * Duplicate notifications of the same seq are OK (at-least-once fan-out /
 * idempotent act acks) and do not advance or demand resync.
 */

export type GaplessOk = {
  status: "ok";
  lastSeq: number;
};

export type GaplessResync = {
  status: "resync";
  reason: "gap" | "stale" | "invalid";
  expected: number;
  got: number;
  lastSeq: number;
};

export type GaplessObserveResult = GaplessOk | GaplessResync;

/**
 * Observe one inbound server-assigned seq against the client's last seen seq.
 *
 * @param lastSeq - last contiguous seq the client has reduced, or `null` if
 *   this is the first observation after attach/snapshot.
 * @param nextSeq - the seq just received (from `session.action` or act ack).
 */
export function observeGaplessSeq(
  lastSeq: number | null,
  nextSeq: number,
): GaplessObserveResult {
  if (!Number.isInteger(nextSeq) || nextSeq < 1) {
    return {
      status: "resync",
      reason: "invalid",
      expected: (lastSeq ?? 0) + 1,
      got: nextSeq,
      lastSeq: lastSeq ?? 0,
    };
  }

  if (lastSeq == null) {
    // First observation after attach may start mid-stream (snapshot floor + 1
    // or a single act ack). Accept any positive seq; subsequent ones must be
    // gapless from here.
    return { status: "ok", lastSeq: nextSeq };
  }

  if (nextSeq === lastSeq) {
    // Duplicate notification / idempotent act ack — not a gap.
    return { status: "ok", lastSeq };
  }

  if (nextSeq === lastSeq + 1) {
    return { status: "ok", lastSeq: nextSeq };
  }

  if (nextSeq < lastSeq) {
    return {
      status: "resync",
      reason: "stale",
      expected: lastSeq + 1,
      got: nextSeq,
      lastSeq,
    };
  }

  // nextSeq > lastSeq + 1
  return {
    status: "resync",
    reason: "gap",
    expected: lastSeq + 1,
    got: nextSeq,
    lastSeq,
  };
}

export type GaplessStreamOk = { ok: true; lastSeq: number };
export type GaplessStreamFail = {
  ok: false;
  reason: "gap" | "stale" | "invalid";
  expected: number;
  got: number;
  lastSeq: number;
  atIndex: number;
};
export type GaplessStreamResult = GaplessStreamOk | GaplessStreamFail;

/**
 * Assert a sequence of seqs is gapless from the first element.
 * Used by tests and by clients that buffer a replay window before reducing.
 */
export function assertGaplessSeqStream(
  seqs: readonly number[],
  startLast: number | null = null,
): GaplessStreamResult {
  let last: number | null = startLast;
  for (let i = 0; i < seqs.length; i++) {
    const r = observeGaplessSeq(last, seqs[i]!);
    if (r.status === "resync") {
      return {
        ok: false,
        reason: r.reason,
        expected: r.expected,
        got: r.got,
        lastSeq: r.lastSeq,
        atIndex: i,
      };
    }
    last = r.lastSeq;
  }
  return { ok: true, lastSeq: last ?? 0 };
}
